"use client";

import type { FormEvent } from "react";
import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          size: "compact" | "flexible" | "normal";
          appearance?: "always" | "execute" | "interaction-only";
          execution?: "execute" | "render";
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
        }
      ) => string;
      execute: (widgetId: string) => Promise<string> | string;
      reset: (widgetId: string) => void;
    };
  }
}

export function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [referrer, setReferrer] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef("");
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";
  const REFERRER_STORAGE_KEY = "waitlist_referrer";

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const rawRef = params.get("ref");
    if (rawRef) {
      const normalized = rawRef.trim().toLowerCase();
      if (normalized) {
        setReferrer(normalized);
        try {
          window.localStorage.setItem(REFERRER_STORAGE_KEY, normalized);
        } catch {
          // Ignore storage errors to avoid blocking signups.
        }
        return;
      }
    }

    try {
      const stored = window.localStorage.getItem(REFERRER_STORAGE_KEY);
      if (!stored) {
        return;
      }
      const normalized = stored.trim().toLowerCase();
      if (normalized) {
        setReferrer(normalized);
      } else {
        window.localStorage.removeItem(REFERRER_STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors to avoid blocking signups.
    }
  }, []);

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileRef.current) {
      return;
    }

    const renderWidget = () => {
      if (!turnstileRef.current || widgetIdRef.current || !window.turnstile) {
        return;
      }
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        size: "compact",
        appearance: "execute",
        callback: (token) => {
          tokenRef.current = token;
        },
        "error-callback": () => {
          tokenRef.current = "";
        },
        "expired-callback": () => {
          tokenRef.current = "";
        }
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>("script[data-turnstile]");
    if (existingScript) {
      existingScript.addEventListener("load", renderWidget);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.turnstile = "true";
    script.addEventListener("load", renderWidget);
    document.body.appendChild(script);
  }, [turnstileSiteKey]);

  const getTurnstileToken = async () => {
    if (!turnstileSiteKey) {
      throw new Error("Turnstile site key missing.");
    }
    const widgetId = widgetIdRef.current;
    if (!window.turnstile || !widgetId) {
      throw new Error("Turnstile not ready.");
    }
    tokenRef.current = "";
    window.turnstile.reset(widgetId);
    const executeResult = window.turnstile.execute(widgetId);
    if (executeResult && typeof executeResult === "string") {
      return executeResult;
    }
    if (executeResult && typeof (executeResult as Promise<string>).then === "function") {
      const token = await executeResult;
      if (token) {
        return token;
      }
    }
    return await new Promise<string>((resolve, reject) => {
      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (tokenRef.current) {
          const token = tokenRef.current;
          tokenRef.current = "";
          window.clearInterval(timer);
          resolve(token);
          return;
        }
        if (Date.now() - startedAt > 8000) {
          window.clearInterval(timer);
          reject(new Error("Turnstile timed out."));
        }
      }, 100);
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!email || !email.includes("@")) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }

    setStatus("loading");

    try {
      const turnstileToken = await getTurnstileToken();
      const payload: { email: string; turnstileToken: string; referrer?: string } = {
        email,
        turnstileToken
      };
      if (referrer) {
        payload.referrer = referrer;
      }
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        setMessage("You're on the list! Check your email for updates.");
        setEmail("");
      } else if (response.status === 403) {
        setStatus("error");
        setMessage("Bot verification failed. Please try again.");
      } else {
        setStatus("error");
        setMessage(data.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setStatus("error");
      setMessage("Unable to verify at the moment. Please try again.");
    }
  };

  return (
    <div className="mx-auto w-full max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="h-11 flex-1 rounded-md border border-slate-300 bg-white px-4 text-slate-900 placeholder:text-slate-400 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/20 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={status === "loading"}
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="h-11 whitespace-nowrap rounded-md bg-gradient-to-r from-indigo-600 to-violet-600 px-6 font-semibold text-white transition-opacity duration-300 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {status === "loading" ? "Joining..." : "Join the waitlist"}
          </button>
        </div>

        {status === "success" ? <p className="text-center text-sm text-green-600">{message}</p> : null}
        {status === "error" ? <p className="text-center text-sm text-red-600">{message}</p> : null}
      </form>

      <div ref={turnstileRef} />

      <p className="mt-4 text-center text-xs text-slate-500">No spam. No noise. Just early access.</p>
    </div>
  );
}
